<template>
  <div>
    <page-heading :title="$t(`${translationPrefix}.title`)"></page-heading>

    <el-card v-if="completedUser">
      <div class="completion">
        <i class="el-icon-circle-check completion__icon"></i>
        <h2>User created successfully</h2>
        <p>
          {{ completedUser.profile.first_name }} {{ completedUser.profile.last_name }}
          ({{ completedUser.email }}) has been created.
        </p>
        <el-alert
          v-if="invitationErrors.length"
          title="The user was created, but one or more invitations could not be sent."
          :description="`Retry invitations from the user or application settings page: ${invitationErrors.join(', ')}`"
          type="warning"
          show-icon
          :closable="false">
        </el-alert>
        <div class="completion__actions">
          <el-button @click="createAnother">Add another user</el-button>
          <el-button @click="$router.push(`/user/${completedUser.id}/profile`)">View user</el-button>
          <el-button type="primary" @click="$router.push('/users')">Return to users</el-button>
        </div>
      </div>
    </el-card>

    <el-card v-else>
      <el-alert
        v-if="errorMessage"
        title="Please review the form"
        :description="errorMessage"
        type="error"
        show-icon
        :closable="false">
      </el-alert>

      <el-steps :active="step" finish-status="success" align-center class="onboarding-steps">
        <el-step title="Identity & Employment"></el-step>
        <el-step title="Application Access"></el-step>
        <el-step title="Optional Profile"></el-step>
        <el-step title="Review"></el-step>
      </el-steps>

      <div v-show="step === 0" class="step-content">
        <h3>Identity and employment</h3>
        <p class="step-description">Enter the information required to create and identify this employee.</p>

        <el-form
          ref="identityForm"
          :model="identity"
          :rules="identityRules"
          label-position="top">
          <el-row :gutter="20">
            <el-col :md="12" :sm="24">
              <el-form-item label="First name" prop="first_name">
                <el-input v-model="identity.first_name" autocomplete="given-name"></el-input>
              </el-form-item>
            </el-col>
            <el-col :md="12" :sm="24">
              <el-form-item label="Last name" prop="last_name">
                <el-input v-model="identity.last_name" autocomplete="family-name"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :md="12" :sm="24">
              <el-form-item :label="$t('modules.user.fields.email.label')" prop="email">
                <el-input type="email" v-model.trim="identity.email" autocomplete="email"></el-input>
              </el-form-item>
            </el-col>
            <el-col :md="6" :sm="12">
              <el-form-item label="Employment type" prop="employment_type">
                <el-select v-model="identity.employment_type" class="full-width">
                  <el-option label="Full Time" value="full_time"></el-option>
                  <el-option label="Half Time" value="half_time"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :md="6" :sm="12">
              <el-form-item label="Joining date" prop="joining_date">
                <el-date-picker
                  v-model="identity.joining_date"
                  type="date"
                  value-format="yyyy-MM-dd"
                  format="yyyy-MM-dd"
                  class="full-width">
                </el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :md="12" :sm="24">
              <el-form-item label="Temporary password" prop="password">
                <el-input
                  type="password"
                  v-model="identity.password"
                  autocomplete="new-password"
                  show-password>
                </el-input>
                <div class="field-help">At least 8 characters. Share it securely with the employee.</div>
              </el-form-item>
            </el-col>
            <el-col :md="12" :sm="24">
              <el-form-item label="Confirm temporary password" prop="password_confirmation">
                <el-input
                  type="password"
                  v-model="identity.password_confirmation"
                  autocomplete="new-password"
                  show-password>
                </el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :md="12" :sm="24">
              <el-form-item :label="$t('modules.user.fields.locale.label')" prop="locale">
                <el-select v-model="identity.locale" filterable class="full-width">
                  <el-option
                    v-for="(localeConfig, locale) in locales"
                    :key="locale"
                    :value="locale"
                    :label="localeConfig.name">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :md="12" :sm="24">
              <el-form-item :label="$t('modules.user.fields.timezone.label')" prop="timezone">
                <el-select v-model="identity.timezone" filterable class="full-width">
                  <el-option
                    v-for="timezone in timezones"
                    :key="timezone"
                    :value="timezone"
                    :label="timezone">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-collapse>
            <el-collapse-item title="Advanced account permissions" name="permissions">
              <el-alert
                title="These permissions provide broad access. Application roles are configured in the next step."
                type="warning"
                :closable="false">
              </el-alert>
              <el-row :gutter="20" class="permission-switches">
                <el-col :span="12">
                  <el-form-item :label="$t('modules.user.fields.admin.label')">
                    <el-switch v-model="identity.admin"></el-switch>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item :label="$t('modules.user.fields.master.label')">
                    <el-switch v-model="identity.master"></el-switch>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-collapse-item>
          </el-collapse>
        </el-form>
      </div>

      <div v-show="step === 1" class="step-content">
        <h3>Application access</h3>
        <p class="step-description">Choose the applications this employee can use and one role for each application.</p>

        <content-placeholder v-if="loadingApps"></content-placeholder>
        <el-alert
          v-else-if="availableApps.length === 0"
          title="No subscribed applications are available for this company."
          type="warning"
          show-icon
          :closable="false">
        </el-alert>
        <div v-else>
          <el-card v-for="app in availableApps" :key="app.id" class="app-access-card">
            <el-row type="flex" align="middle" :gutter="20">
              <el-col :md="10" :sm="24">
                <el-checkbox v-model="appAssignments[app.id].selected">
                  <strong>{{ app.name }}</strong>
                </el-checkbox>
              </el-col>
              <el-col :md="14" :sm="24">
                <el-select
                  v-model="appAssignments[app.id].role_id"
                  :disabled="!appAssignments[app.id].selected"
                  placeholder="Select a role"
                  class="full-width">
                  <el-option
                    v-for="role in rolesForApp(app)"
                    :key="role.id"
                    :label="role.name"
                    :value="role.id">
                  </el-option>
                </el-select>
              </el-col>
            </el-row>
          </el-card>
        </div>
      </div>

      <div v-show="step === 2" class="step-content">
        <h3>Optional profile details</h3>
        <p class="step-description">These fields already exist in the employee profile and may be completed now or later.</p>

        <el-form ref="profileForm" :model="profile" :rules="profileRules" label-position="top">
          <el-row :gutter="20">
            <el-col :md="12" :sm="24">
              <el-form-item label="Job title" prop="title">
                <el-input v-model="profile.title"></el-input>
              </el-form-item>
            </el-col>
            <el-col :md="12" :sm="24">
              <el-form-item label="Phone" prop="phone">
                <el-input v-model="profile.phone" autocomplete="tel"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :md="12" :sm="24">
              <el-form-item label="Address" prop="address">
                <el-input v-model="profile.address" autocomplete="address-line1"></el-input>
              </el-form-item>
            </el-col>
            <el-col :md="12" :sm="24">
              <el-form-item label="Address line 2" prop="address2">
                <el-input v-model="profile.address2" autocomplete="address-line2"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :md="10" :sm="24">
              <el-form-item label="City" prop="city">
                <el-input v-model="profile.city" autocomplete="address-level2"></el-input>
              </el-form-item>
            </el-col>
            <el-col :md="8" :sm="12">
              <el-form-item label="State" prop="state">
                <el-select v-model="profile.state" filterable clearable class="full-width">
                  <el-option
                    v-for="state in states"
                    :key="state.abbreviation || state.name"
                    :label="state.name"
                    :value="state.name">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :md="6" :sm="12">
              <el-form-item label="ZIP code" prop="zip">
                <el-input v-model.trim="profile.zip" autocomplete="postal-code"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :md="12" :sm="24">
              <el-form-item label="Social Security number (optional and sensitive)" prop="ssn">
                <el-input
                  type="password"
                  v-model.trim="profile.ssn"
                  autocomplete="off"
                  show-password>
                </el-input>
                <div class="field-help">Only enter this when required for employee administration.</div>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>

      <div v-show="step === 3" class="step-content">
        <h3>Review and create</h3>
        <p class="step-description">Confirm the information below. The employee and all selected access assignments will be created together.</p>

        <el-card class="review-section">
          <div slot="header"><strong>Employee</strong></div>
          <p>{{ identity.first_name }} {{ identity.last_name }}</p>
          <p>{{ identity.email }}</p>
          <p>{{ employmentTypeLabel }} · Joining {{ identity.joining_date }}</p>
          <p>{{ identity.locale }} · {{ identity.timezone }}</p>
        </el-card>

        <el-card class="review-section">
          <div slot="header"><strong>Application access</strong></div>
          <p v-for="assignment in selectedAssignmentDetails" :key="assignment.app.id">
            {{ assignment.app.name }} — {{ assignment.role ? assignment.role.name : 'Role not selected' }}
          </p>
        </el-card>

        <el-card class="review-section">
          <div slot="header"><strong>Optional profile</strong></div>
          <p>{{ profile.title || 'No job title provided' }}</p>
          <p>{{ profile.phone || 'No phone provided' }}</p>
          <p>{{ formattedAddress || 'No address provided' }}</p>
          <p>SSN: {{ profile.ssn ? 'Provided (encrypted at rest)' : 'Not provided' }}</p>
        </el-card>
      </div>

      <div class="wizard-actions">
        <el-button v-if="step > 0" @click="previousStep" :disabled="submitting">Back</el-button>
        <div class="wizard-actions__right">
          <el-button @click="$router.push('/users')" :disabled="submitting">Cancel</el-button>
          <el-button
            v-if="step < 3"
            type="primary"
            @click="nextStep"
            :loading="submitting">
            Continue
          </el-button>
          <el-button
            v-else
            type="primary"
            @click="submitOnboarding"
            :loading="submitting">
            Create user and send invitation
          </el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import moment from 'moment';
  import 'moment-timezone';

  import availableLocales from '@/i18n/available_locales';
  import USStates from '@/mixins/states_us';
  import validations from '@/utils/validations';

  const emptyProfile = () => ({
    title: '',
    phone: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    ssn: '',
  });

  export default {
    name: 'UserCreate',
    mixins: [USStates],
    data() {
      const validatePasswordConfirmation = (rule, value, callback) => {
        if (value !== this.identity.password) {
          callback(new Error('Passwords must match'));
        } else {
          callback();
        }
      };

      return {
        translationPrefix: 'pages.userCreate',
        step: 0,
        submitting: false,
        loadingApps: false,
        errorMessage: '',
        completedUser: null,
        invitationErrors: [],
        locales: availableLocales,
        timezones: moment.tz.names(),
        states: this.getStates(),
        appAssignments: {},
        identity: {
          first_name: '',
          last_name: '',
          email: '',
          employment_type: 'full_time',
          joining_date: moment().format('YYYY-MM-DD'),
          password: '',
          password_confirmation: '',
          locale: 'en',
          timezone: moment.tz.guess() || 'America/Detroit',
          admin: false,
          master: false,
        },
        profile: emptyProfile(),
        identityRules: {
          first_name: [{ ...validations.required }],
          last_name: [{ ...validations.required }],
          email: [{ ...validations.required }, { ...validations.email }],
          employment_type: [{ ...validations.required }],
          joining_date: [{ ...validations.required }],
          locale: [{ ...validations.required }],
          timezone: [{ ...validations.required }],
          password: [
            { ...validations.required },
            { min: 8, message: 'Password must be at least 8 characters', trigger: 'blur' },
          ],
          password_confirmation: [
            { ...validations.required },
            { validator: validatePasswordConfirmation, trigger: 'blur' },
          ],
        },
        profileRules: {
          phone: [
            { pattern: /^[0-9()+\-\s.]*$/, message: 'Enter a valid phone number', trigger: 'blur' },
          ],
          zip: [
            { pattern: /^\d{5}(-\d{4})?$|^$/, message: 'Enter a valid ZIP code', trigger: 'blur' },
          ],
          ssn: [
            { pattern: /^\d{3}-?\d{2}-?\d{4}$|^$/, message: 'Enter a valid SSN or leave it blank', trigger: 'blur' },
          ],
        },
      };
    },
    computed: {
      apps() {
        return this.$store.getters['entities/apps/query']().with('roles').with('app_memberships').get();
      },
      availableApps() {
        const appsByIdentity = {};

        this.apps
          .filter(app => app.app_memberships && app.app_memberships.length > 0)
          .forEach((app) => {
            const identity = (app.name || app.machine_name || app.id.toString()).toLowerCase().trim();
            const existing = appsByIdentity[identity];

            if (!existing || this.rolesForApp(app).length > this.rolesForApp(existing).length) {
              appsByIdentity[identity] = app;
            }
          });

        return Object.keys(appsByIdentity).map(identity => appsByIdentity[identity]);
      },
      selectedAssignments() {
        return this.availableApps
          .filter(app => this.appAssignments[app.id] && this.appAssignments[app.id].selected)
          .map(app => ({
            app_id: app.id,
            role_id: this.appAssignments[app.id].role_id,
          }));
      },
      selectedAssignmentDetails() {
        return this.selectedAssignments.map((assignment) => {
          const app = this.availableApps.find(item => item.id === assignment.app_id);
          const role = this.rolesForApp(app).find(item => item.id === assignment.role_id);
          return { app, role };
        });
      },
      employmentTypeLabel() {
        return this.identity.employment_type === 'half_time' ? 'Half Time' : 'Full Time';
      },
      formattedAddress() {
        return [
          this.profile.address,
          this.profile.address2,
          this.profile.city,
          this.profile.state,
          this.profile.zip,
        ].filter(Boolean).join(', ');
      },
    },
    created() {
      this.loadApplications();
    },
    methods: {
      ...mapActions('entities/apps', ['fetchApps']),
      ...mapActions('entities/users', ['onboardUser']),

      loadApplications() {
        this.loadingApps = true;
        this.fetchApps()
          .then((response) => {
            if (response.status) {
              this.initializeAssignments();
            } else {
              this.errorMessage = 'Applications could not be loaded. Please try again.';
            }
            this.loadingApps = false;
          });
      },
      initializeAssignments() {
        this.availableApps.forEach((app) => {
          if (!this.appAssignments[app.id]) {
            this.$set(this.appAssignments, app.id, {
              selected: false,
              role_id: null,
            });
          }
        });
      },
      rolesForApp(app) {
        if (!app || !app.roles) return [];
        const rolesById = {};
        app.roles.forEach((role) => {
          if (role.app_id === app.id) rolesById[role.id] = role;
        });
        return Object.keys(rolesById).map(id => rolesById[id]);
      },
      validateForm(ref) {
        return new Promise((resolve) => {
          this.$refs[ref].validate(valid => resolve(valid));
        });
      },
      validateApplicationAccess() {
        if (this.selectedAssignments.length === 0) {
          this.errorMessage = 'Select at least one application.';
          return false;
        }

        const missingRole = this.selectedAssignments.some(assignment => !assignment.role_id);
        if (missingRole) {
          this.errorMessage = 'Select a role for every selected application.';
          return false;
        }

        this.errorMessage = '';
        return true;
      },
      async nextStep() {
        this.errorMessage = '';

        if (this.step === 0 && !await this.validateForm('identityForm')) {
          this.errorMessage = 'Complete the required identity and employment fields.';
          return;
        }
        if (this.step === 1 && !this.validateApplicationAccess()) return;
        if (this.step === 2 && !await this.validateForm('profileForm')) {
          this.errorMessage = 'Correct the invalid optional profile fields or leave them blank.';
          return;
        }

        this.step += 1;
        window.scrollTo(0, 0);
      },
      previousStep() {
        this.errorMessage = '';
        this.step -= 1;
        window.scrollTo(0, 0);
      },
      async submitOnboarding() {
        if (!this.validateApplicationAccess()) {
          this.step = 1;
          return;
        }

        this.submitting = true;
        this.errorMessage = '';

        const user = {
          email: this.identity.email,
          password: this.identity.password,
          password_confirmation: this.identity.password_confirmation,
          locale: this.identity.locale,
          timezone: this.identity.timezone,
          employment_type: this.identity.employment_type,
          joining_date: this.identity.joining_date,
          admin: this.identity.admin,
          master: this.identity.master,
        };
        const profile = {
          ...this.profile,
          address: this.profile.address.trim(),
          address2: this.profile.address2.trim(),
          city: this.profile.city.trim(),
          title: this.profile.title.trim(),
          phone: this.profile.phone.trim(),
          first_name: this.identity.first_name.trim(),
          last_name: this.identity.last_name.trim(),
        };

        const response = await this.onboardUser({
          user,
          profile,
          app_assignments: this.selectedAssignments,
        });

        this.submitting = false;
        if (response.status) {
          this.completedUser = response.data.entity;
          this.invitationErrors = response.data.invitation_errors || [];
          window.scrollTo(0, 0);
        } else {
          this.errorMessage = Array.isArray(response.error)
            ? response.error.join(', ')
            : response.error;
        }
      },
      createAnother() {
        window.location.reload();
      },
    },
    watch: {
      apps() {
        this.initializeAssignments();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .onboarding-steps {
    margin: 10px 0 32px;
  }

  .step-content {
    min-height: 420px;
  }

  .step-description,
  .field-help {
    color: #606266;
  }

  .field-help {
    font-size: 12px;
    line-height: 1.4;
    margin-top: 4px;
  }

  .permission-switches {
    margin-top: 16px;
  }

  .app-access-card,
  .review-section {
    margin-bottom: 16px;
  }

  .wizard-actions {
    border-top: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
    padding-top: 20px;
  }

  .wizard-actions__right {
    margin-left: auto;
  }

  .completion {
    padding: 40px;
    text-align: center;
  }

  .completion__icon {
    color: #67c23a;
    font-size: 64px;
  }

  .completion__actions {
    margin-top: 28px;
  }
</style>



// WEBPACK FOOTER //
// src/pages/UserCreate.vue